import { combineReducers } from 'redux';
import { call, put, putResolve, select, takeLatest } from 'redux-saga/effects';

import { api } from './api';
import { environment } from 'environments/environment';

import { ExtendedAxiosResponse } from 'helpers/apiClient';
import {
  AppAction,
  createActionCreator,
  createActionType,
  createApiActionCreators,
  createReducer,
  RequestActionTypes,
} from 'helpers/redux/reduxHelpers';
import { AppState } from 'helpers/store/models/AppState';
import { ApiClientRequest } from 'models/ApiClientRequest';
import { ClearContextEntityType, ClearContextSuccessActionInputs } from 'models/context/ClearContext';
import { CustomerRequestInfo } from 'models/context/CustomerRequestInfo';
import { LegacyCrmDeeplinkCommand, LegacyCrmDeeplinkConfig } from 'models/LegacyCrmDeeplink';
import { getApiUrl, performApiCall } from 'saga/apiClient/ducks';
import { AuthActionTypes, selectIsUserInternal } from 'saga/auth/ducks';
import { redirectToLegacyCrmAction } from 'saga/crm-deeplinks/ducks';
import { selectProperty } from 'saga/property/ducks';
import { clearContextAction, ContextActionTypes } from 'saga/context/ducks';

export interface OpenCrCreationPayload {
  crType: string;
  crSubType: string;
  queryParams?: Record<string, string>;
}

/* STATE */
export interface ContextCustomerRequestState {
  crInfo: CustomerRequestInfo | null;
  crCreationParams: OpenCrCreationPayload | null;
}

/* ACTION TYPES */
export enum ContextCustomerRequestActionTypes {
  SetContextCustomerRequest = '@@Context/CustomerRequest/SET_CONTEXT_CUSTOMER_REQUEST',
  OpenCustomerRequestSidePanel = '@@Context/CustomerRequest/OPEN_CUSTOMER_REQUEST_SIDE_PANEL',
  OpenCrCreation = '@@Context/CustomerRequest/OPEN_CUSTOMER_REQUEST_CREATION',
}

/* ACTIONS */
export const setContextCustomerRequestAction = createActionCreator(
  ContextCustomerRequestActionTypes.SetContextCustomerRequest
);
export const openCustomerRequestSidePanelAction = createActionCreator(
  ContextCustomerRequestActionTypes.OpenCustomerRequestSidePanel
);
export const openCrCreationActions = createApiActionCreators(ContextCustomerRequestActionTypes.OpenCrCreation);

/* REDUCERS */
const initialState: ContextCustomerRequestState = {
  crInfo: null,
  crCreationParams: null,
};

const crInfo = createReducer(initialState.crInfo, {
  [ContextActionTypes.ClearContext]: {
    [RequestActionTypes.SUCCESS]: (_state: CustomerRequestInfo | null, payload: ClearContextSuccessActionInputs) =>
      payload.clearContextEntity === ClearContextEntityType.CUSTOMER_REQUEST ? null : _state,
    [RequestActionTypes.FAILURE]: (_state: CustomerRequestInfo | null) => _state,
  },
  [ContextCustomerRequestActionTypes.SetContextCustomerRequest]: (
    _state: CustomerRequestInfo | null,
    payload: CustomerRequestInfo
  ) => payload,
  [AuthActionTypes.Logout]: {
    [RequestActionTypes.SUCCESS]: () => null,
  },
});

const crCreationParams = createReducer(initialState.crCreationParams, {
  [ContextActionTypes.ClearContext]: {
    [RequestActionTypes.SUCCESS]: (_state: OpenCrCreationPayload | null, payload: ClearContextSuccessActionInputs) =>
      payload.clearContextEntity === ClearContextEntityType.CUSTOMER_REQUEST ? null : _state,
    [RequestActionTypes.FAILURE]: (_state: OpenCrCreationPayload | null) => _state,
  },
  [ContextCustomerRequestActionTypes.OpenCrCreation]: {
    [RequestActionTypes.SUCCESS]: (_state: OpenCrCreationPayload | null, payload: OpenCrCreationPayload) => payload,
  },
});

export default combineReducers<ContextCustomerRequestState>({
  crInfo,
  crCreationParams,
});

/* SELECTORS */
const selectContextCustomerRequest = (state: AppState) => state.context.customerRequest || {};

export const selectContextCustomerRequestInfo = (state: AppState): CustomerRequestInfo | null =>
  selectContextCustomerRequest(state).crInfo;

const selectCrCreationParams = (state: AppState): OpenCrCreationPayload | null =>
  selectContextCustomerRequest(state).crCreationParams;

export const selectContextCustomerRequestDeeplinkUrl = (state: AppState): string | undefined => {
  const onecrmPzUrl: string = selectProperty(environment.urlConfig.pzForIframeApf2.propertyKey);
  const crInfo: CustomerRequestInfo | null = selectContextCustomerRequestInfo(state);

  if (crInfo?.crRefNo && onecrmPzUrl) {
    return `${onecrmPzUrl}${crInfo.crRefNo}`;
  }

  const crCreationPayload = selectCrCreationParams(state);
  let onecrmPzCreationUrl: string = selectProperty(environment.urlConfig.pzForIframeApf2CreateCr.propertyKey);

  if (crCreationPayload && onecrmPzCreationUrl) {
    const creationParams: Record<string, string> = {
      ...crCreationPayload.queryParams,
      crType: crCreationPayload.crType,
      crSubType: crCreationPayload.crSubType,
    };

    for (const param in creationParams) {
      onecrmPzCreationUrl += `&${param}=${creationParams[param]}`;
    }

    return onecrmPzCreationUrl;
  }

  return undefined;
};

/* SAGAS */
function* openCustomerRequest({ payload: crRefNo }: AppAction<string>) {
  // initialize property before use in selector
  selectProperty(environment.urlConfig.pzForIframeApf2.propertyKey);
  yield put(setContextCustomerRequestAction({ crRefNo: crRefNo }));
}

/**
 * EA: Projects.O2 Czech Republic.MO2-CARE.Processes.Common.Entities.CustomerRequest.openCrCreation.openCrCreation
 */
function* openCrCreation({ payload }: AppAction<OpenCrCreationPayload>) {
  yield put(clearContextAction.request({ clearContextEntity: ClearContextEntityType.CUSTOMER_REQUEST }));

  const isInternal: boolean = yield select(selectIsUserInternal);

  if (isInternal) {
    const request: ApiClientRequest = {
      apiCall: call(api.openCR, yield getApiUrl(environment.urlConfig.pzForIframeApf2CreateCr), payload),
    };
    const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

    if (resp.ok) {
      yield put(openCrCreationActions.success(payload));
    }
  } else {
    const deeplinkParams: LegacyCrmDeeplinkConfig = {
      command: LegacyCrmDeeplinkCommand.EMO2DASH,
      queryParams: {
        ...payload.queryParams,
        action: 'createCustomerRequest',
        crType: payload.crType,
        crSubType: payload.crSubType,
        logToVisitedHistory: 'true',
      },
    };

    yield putResolve(redirectToLegacyCrmAction(deeplinkParams));
  }
}

/* EXPORT */
export function* contextCustomerRequestSaga() {
  yield takeLatest(
    createActionType(ContextCustomerRequestActionTypes.OpenCustomerRequestSidePanel),
    openCustomerRequest
  );

  yield takeLatest(
    createActionType(ContextCustomerRequestActionTypes.OpenCrCreation, RequestActionTypes.REQUEST),
    openCrCreation
  );
}
